import { useMemo } from 'react';
import { TabItem } from '../../components/components.interfaces';
import { MessagingProvider } from './contexts/messaging.context';
import type { FC } from 'react';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { hasReadPermission } from 'src/utils/rbacHelpers';
import { PermissionGate } from '../../../components/rbac/PermissionGate';
import { RbacDeniedRedirect } from '../../../components/rbac/RbacDeniedRedirect';

export const communicationsSubRouteIds = [
  'messaging',
  'announcements',
  'concerns',
  'events',
  'meetings',
];

export const useCommunicationsTabMenuItems = (
  translateText: TTranslateTextType
): TabItem[] => {
  const { rbac } = useRbacState();

  return useMemo(() => {
    const MessagingView = require('./subviews/messaging/messaging.view')
      .default as FC;
    const AnnouncementsView =
      require('./subviews/announcements/announcements.view').default as FC;
    const ConcernsView =
      require('./subviews/concerns/ConcernsView.view').default as FC<{
        isGuardian?: boolean;
      }>;

    const tabs: TabItem[] = [];

    if (hasReadPermission(rbac, 'communications', 'messaging')) {
      tabs.push({
        label: translateText('communications.tabs.messaging'),
        onClickHandler: () => {},
        tabPanel: (
          <MessagingProvider>
            <MessagingView />
          </MessagingProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'communications', 'announcements')) {
      tabs.push({
        label: translateText('communications.tabs.announcements'),
        onClickHandler: () => {},
        tabPanel: <AnnouncementsView />,
      });
    }

    if (hasReadPermission(rbac, 'communications', 'concerns')) {
      tabs.push({
        label: translateText('communications.tabs.concerns'),
        onClickHandler: () => {},
        tabPanel: (
          <PermissionGate
            module="communications"
            submodule="concerns"
            permission="any"
            fallback={<RbacDeniedRedirect />}
          >
            <ConcernsView isGuardian={false} />
          </PermissionGate>
        ),
      });
    }

    if (hasReadPermission(rbac, 'communications', 'events')) {
      tabs.push({
        label: translateText('communications.tabs.events'),
        onClickHandler: () => {},
        tabPanel: translateText('communications.events.description'),
      });
    }

    if (hasReadPermission(rbac, 'communications', 'meetings')) {
      tabs.push({
        label: translateText('communications.tabs.meetings'),
        onClickHandler: () => {},
        tabPanel: translateText('communications.meetings.description'),
      });
    }

    return tabs;
  }, [rbac, translateText]);
};

/** Returns route IDs for tabs the user has read permission for, in display order */
export const useCommunicationsAvailableRouteIds = (): string[] => {
  const { rbac } = useRbacState();
  return useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'communications', 'messaging')) routes.push('messaging');
    if (hasReadPermission(rbac, 'communications', 'announcements')) routes.push('announcements');
    if (hasReadPermission(rbac, 'communications', 'concerns')) routes.push('concerns');
    if (hasReadPermission(rbac, 'communications', 'events')) routes.push('events');
    if (hasReadPermission(rbac, 'communications', 'meetings')) routes.push('meetings');
    return routes;
  }, [rbac]);
};

export const getCommunicationsTabIndex = (pathname: string) => {
  const idx = communicationsSubRouteIds.indexOf(pathname);
  return idx >= 0 ? idx : 0;
};

export const getCommunicationsSubRouteId = (index: number) =>
  communicationsSubRouteIds[index];
